import { useCallback, useEffect, useMemo, useState } from "react";
import { convertUTCtoLocalTime } from "../helpers/dateTimeHelper";
import { getCurrentUserFromLocalStorage } from "../helpers/localStorageHelper";
import { PASS_BANNER_STATES } from "../constants/pass";
import { getActivePass, getAvailablePassProducts } from "../services/passService";

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

const getRemainingDays = (activePass) => {
  if (!activePass) return null;
  if (typeof activePass.remainingDays === "number") return activePass.remainingDays;

  const endDate = convertUTCtoLocalTime(activePass.learningEndDate);
  if (!endDate) return null;

  const diff = Math.ceil((endDate.getTime() - Date.now()) / MILLISECONDS_PER_DAY);
  return diff > 0 ? diff : 0;
};

export default function usePass() {
  const [passProducts, setPassProducts] = useState([]);
  const [activePass, setActivePass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const isAuthenticated = !!getCurrentUserFromLocalStorage();

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);

    const [productsResult, activeResult] = await Promise.allSettled([
      getAvailablePassProducts(),
      isAuthenticated ? getActivePass() : Promise.resolve(null),
    ]);

    if (productsResult.status === "fulfilled") {
      setPassProducts(productsResult.value ?? []);
    } else {
      setPassProducts([]);
    }

    if (activeResult.status === "fulfilled") {
      setActivePass(activeResult.value ?? null);
    } else {
      setActivePass(null);
    }

    setFailed(productsResult.status === "rejected" && activeResult.status === "rejected");
    setLoading(false);
  }, [isAuthenticated]);

  useEffect(() => {
    load();
  }, [load]);

  const remainingDays = useMemo(() => getRemainingDays(activePass), [activePass]);

  const state = useMemo(() => {
    if (loading) return PASS_BANNER_STATES.LOADING;
    if (activePass) return PASS_BANNER_STATES.ACTIVE;
    if (passProducts.length) return PASS_BANNER_STATES.NO_PASS;
    return PASS_BANNER_STATES.UNAVAILABLE;
  }, [loading, activePass, passProducts]);

  return {
    loading,
    failed,
    isAuthenticated,
    passProducts,
    activePass,
    hasActivePass: !!activePass,
    remainingDays,
    state,
    reload: load,
  };
}
